import {
  createDataItemSigner,
  message,
  monitor,
  result,
  unmonitor,
} from "@permaweb/aoconnect"

import { dryrun } from "./ao-connection"

import { Message } from "./ao"
import { getLinkedMessages } from "./messages-api"
import { BasePool, getOnchainPoolReserves } from "./pools-api"
import { PoolParams, TokenInfo } from "./token-api"
import ENV from "@/env"
import { LONG_OPERATION } from "@/settings"
import { transfer } from "@/utils/bark"
import { BackendError, retryable } from "@/utils/utils"

export type PortfolioAgentStatus =
  | "AwaitingStrategyConfiguration"
  | "AwaitingInitialDeposit"
  | "AwaitingSubscriptions"
  | "Ready"
  | "Rebalancing"
  | "Paused"
  | "Liquidating"
  | "Retired"
  | "EmergencyExiting"
  | "EmergencyExited"
  | "Deactivated"
  | "Withdrawing"

async function readAgentId(requestId: string): Promise<string> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, requestId)

  const process = messages.find((msg) => msg.tags["Type"] === "Process")
  if (!process) throw new Error("Agent Id not found")

  console.log("onAgentIdFromGateway", process.id)
  return process.id
}

async function readAgentIdFromCU(requestId: string): Promise<string> {
  try {
    const res = await dryrun({
      process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
      tags: [
        { name: "Action", value: "Get-Agent-Id" },
        { name: "Request-Id", value: requestId },
      ],
    })

    const agentIdResp = JSON.parse(res.Messages[0].Data) as {
      ["Request-Id"]: string
      ["Agent-Id"]: string
    }

    if (!agentIdResp["Agent-Id"]) throw new Error("Agent Id (from CU) not found")

    console.log("onAgentIdFromCU", agentIdResp["Agent-Id"])

    return agentIdResp["Agent-Id"]
  } catch (e) {
    console.error(e)
    throw new Error("Agent Id (from CU) not found")
  }
}

async function readAgentStatus(agentId: string): Promise<PortfolioAgentStatus> {
  const info = await readAgentInfo(agentId)
  return info.Status as PortfolioAgentStatus
}

export interface PortfolioAgentParams {
  agentName: string
  quoteToken: TokenInfo
  amount: bigint
  deviation: string
  slippage: string | undefined
  portfolioTokens: TokenInfo[]
  pools: Record<string, string>
  weights: Record<string, string>
  quoteTokenUsdPrice: number
}

async function waitForAgentAwaitingInitialDeposit(agentId: string): Promise<PortfolioAgentStatus> {
  const info = await readAgentInfo(agentId)
  info.Status as PortfolioAgentStatus

  if (info.Status === "AwaitingInitialDeposit") return info.Status

  throw new Error("Agent not ready")
}

function spawnPayloadFromFormData(data: PortfolioAgentParams): any {
  return {
    "Initial-Deposit": data.amount.toString(),
    "Deviation-Threshold": data.deviation,
    "Slippage-Tolerance": data.slippage,
    "Quote-Token": {
      token: data.quoteToken.id,
      ticker: data.quoteToken.ticker,
      denomination: data.quoteToken.denomination,
    },
    "Portfolio-Tokens": data.portfolioTokens.map((token) => ({
      token: token.id,
      ticker: token.ticker,
      denomination: token.denomination,
      pool: data.pools[token.id],
      weight: data.weights[token.id],
    })),
  }
}

export async function checkSlippageOnFirstRebalance(
  data: PortfolioAgentParams,
): Promise<[boolean, number, string]> {
  type PoolParamsWithToken = { token: string } & PoolParams
  const poolParamsProms: Promise<PoolParamsWithToken>[] = data.portfolioTokens.map(
    async (pToken) => {
      const pool: BasePool = {
        id: data.pools[pToken.id],
        baseToken: pToken.id,
        quoteToken: data.quoteToken.id,
        created: 123,
      }
      const reserves = await getOnchainPoolReserves(pool)
      return {
        token: pToken.id,
        reservesThis: reserves.baseTokenAsString,
        reservesQuote: reserves.quoteTokenAsString,
        totalFee: 0.25,
      }
    },
  )

  const allPoolParams = await Promise.all(poolParamsProms)

  const spawnData = spawnPayloadFromFormData(data)

  for (const pToken of spawnData["Portfolio-Tokens"]) {
    const poolParams = allPoolParams.find(
      (params: PoolParamsWithToken) => params.token === pToken.token,
    )
    pToken.poolParams = poolParams
  }

  const result = await dryrun({
    process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
    tags: [{ name: "Action", value: "Can-Start-With-Acceptable-Slippage" }],
    data: JSON.stringify(spawnData),
  })

  if (result.Messages.length > 0 && result.Messages[0].Data) {
    const resultData = JSON.parse(result.Messages[0].Data)
    const flag = resultData["Can-Start-OK"]
    const isOk = flag === "true" || flag === true
    let maxSlippage = 0
    let assetId = ""
    for (const swap of resultData["Planned-Swaps"]) {
      const slippage = Math.abs(Number(swap.expectedSlippage))
      if (slippage > maxSlippage) {
        maxSlippage = slippage
        assetId = swap.tokenTo
      }
    }
    console.log("onSlippageCheck", resultData, isOk, maxSlippage, assetId)

    return [isOk, maxSlippage, assetId]
  }

  throw new Error("Initial rebalance simulation failed")
}

export async function monitorAgent(agentId: string): Promise<string> {
  try {
    const monitorId = await monitor({
      process: agentId,
      signer: createDataItemSigner(window.arweaveWallet),
    })

    return monitorId
  } catch (err: any) {
    if ("message" in err && err.message.includes("Process already being monitored"))
      return "unknown"
    console.error(err)
    throw new Error("Unable to monitor Agent with cron")
  }
}

export async function unmonitorAgent(agentId: string) {
  try {
    const unmonitorId = await unmonitor({
      process: agentId,
      signer: createDataItemSigner(window.arweaveWallet),
    })

    return unmonitorId
  } catch (err: any) {
    console.error(err)
  }
}

export async function spawnPortfolioAgent(
  data: PortfolioAgentParams,
  onPrepared?: (agentId: string) => void,
): Promise<string> {
  if (typeof data.quoteTokenUsdPrice !== "number")
    throw new Error("Quote token USD price is not a number")

  const payload = spawnPayloadFromFormData(data)
  console.log("onPayload", payload)
  const requestId = await message({
    process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [
      { name: "Action", value: "Create-Agent" },
      { name: "Agent-Name", value: data.agentName },
    ],
    data: JSON.stringify(payload),
  })
  console.log("onRequestId", requestId)

  const computedResult = await result({
    message: requestId,
    process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
  })

  console.log("onRequestComputed", computedResult)

  if (
    computedResult.Messages.length === 0 &&
    computedResult.Spawns.length === 0 &&
    computedResult.Output.data
  ) {
    throw new Error(computedResult.Output.data)
  }

  const agentId = await Promise.race([
    retryable(readAgentId, LONG_OPERATION)(requestId),
    retryable(readAgentIdFromCU, LONG_OPERATION)(requestId),
  ])
  console.log("onAgentId", agentId)

  const status = await retryable(readAgentStatus)(agentId)
  console.log("onAgentStatus", status)

  if (status !== "AwaitingStrategyConfiguration" && status !== "AwaitingInitialDeposit")
    throw new Error("Unexpected Agent status")

  // Ensure that we are ready to deposit
  await retryable(waitForAgentAwaitingInitialDeposit)(agentId)

  // monitor
  const monitorId = await retryable(monitorAgent)(agentId)
  console.log("onMonitorId", monitorId)

  onPrepared?.(agentId)

  const transferId = await transfer(
    {
      token: data.quoteToken.id,
      recipient: agentId,
      quantity: data.amount,
      tags: [
        { name: "X-Action", value: "Deposit" },
        { name: "X-Quote-Token-Price", value: String(data.quoteTokenUsdPrice) },
      ],
    },
    createDataItemSigner(window.arweaveWallet),
  )
  console.log("onTransferId", transferId)

  return agentId
}

export interface PortfolioAgent {
  id: string
  created_at_ts: number
  amm_processes: string[]
  portfolio_owner: string
  quote_token: string
  status: "Initializing" | "Active"
  token_processes: string[]
  weights: string[]
  pnl_7d_abs?: string
  pnl_7d_rel?: number
  pnl_24h_abs?: string
  pnl_24h_rel?: number
  pnl_30d_abs?: string
  pnl_30d_rel?: number
  pnl_all_time_abs?: string
  pnl_all_time_rel?: number
  volume_all_time?: string
  eol_no_funds?: number
}

export async function getPortfolioAgents(activeAddr?: string): Promise<PortfolioAgent[]> {
  const tags = [{ name: "Action", value: "Get-Agents" }]

  if (activeAddr) tags.push({ name: "Portfolio-Owner", value: activeAddr })

  const res = await dryrun({
    process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
    Timestamp: 0,
    tags,
  })

  for (const msg of res.Messages as Message[]) {
    const val = JSON.parse(msg.Data)

    if (val) {
      return val.map(mapPortfolioAgent) as PortfolioAgent[]
    }
  }

  throw new Error("Response malformed")
}

export async function getPortfolioAgentsRanked(): Promise<PortfolioAgent[]> {
  const tags = [
    { name: "Action", value: "Get-Ranked-Agents" },
    { name: "Sort-By", value: "pnl_all_time_rel" },
    // { name: "Status-Filter", value: "active" }, // TODO
  ]

  const res = await dryrun({
    process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
    Timestamp: 0,
    tags,
  })

  for (const msg of res.Messages as Message[]) {
    const val = JSON.parse(msg.Data)

    if (val) {
      return (val.map(mapPortfolioAgent) as PortfolioAgent[]).filter(
        (x) => x.status === "Active" && typeof x.volume_all_time !== "undefined",
      )
    }
  }

  throw new Error("Response malformed")
}

export async function getAggregatedStats(): Promise<{
  agentCount: string
  totalVolume: string
}> {
  const tags = [{ name: "Action", value: "Get-Aggregated-Stats" }]

  const res = await dryrun({
    process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
    Timestamp: 0,
    tags,
  })

  for (const msg of res.Messages as Message[]) {
    const val = JSON.parse(msg.Data)

    if (val) {
      return val as {
        agentCount: string
        totalVolume: string
      }
    }
  }

  throw new Error("Response malformed")
}

function mapPortfolioAgent(agent: any): PortfolioAgent {
  return {
    ...agent,
    weights: JSON.parse(agent.weights),
    amm_processes: JSON.parse(agent.amm_processes),
    token_processes: JSON.parse(agent.token_processes),
  }
}

export async function getPortfolioAgent(agentId: string): Promise<PortfolioAgent> {
  const res = await dryrun({
    process: ENV.VITE_PORTFOLIO_AGENT_FACTORY_PROCESS,
    Timestamp: 0,
    tags: [
      { name: "Action", value: "Get-Agent" },
      { name: "Id", value: agentId },
    ],
  })

  for (const msg of res.Messages as Message[]) {
    const val = JSON.parse(msg.Data)

    if (val) return mapPortfolioAgent(val) as PortfolioAgent
  }

  throw new Error("Response malformed")
}

export interface PortfolioAgentInfo {
  DailyHistory: DailyHistory[]
  PortfolioTokens: PortfolioToken[]
  "Has-Pending-Pause": string
  "Has-Pending-Withdrawal": string
  "Quote-Token-Ticker": string
  Rebalancings: Rebalancing[]
  "Total-Deposited": string
  "Quote-Token-Denomination": string
  "Has-Pending-Liquidation": string
  "Is-Paused-After-High-Slippage": string
  CurrentRebalancing?: RebalanceData
  Status: PortfolioAgentStatus
  "Quote-Token": string
  "Portfolio-Owner": string
  "Initial-Deposit": string
  Holdings: HoldingsData
  "Deviation-Threshold": string
  Deposits: Deposit[]
  Withdrawals: Withdrawal[]
  "Agent-Name": string
  "Slippage-Tolerance": string
}

interface Holdings {
  timestamp: number
  isUnbalanced: boolean
  entries: HoldingEntry[]
  maxRealDeviation: number
  totalValue: string
  realTotalValue: string
  maxDeviation: number
}

interface PlannedBuy {
  idealOut: string
  expectedMinOut: string
  tickerFrom: string
  triggerId: string
  amountIn: string
  expectedSlippage: string
  tokenTo: string
  tokenFrom: string
  pool: string
  tickerTo: string
}

interface RebalanceData {
  reference: number
  triggerAction: string
  plannedSells: any[]
  triggerId: string
  swapsOut: any[]
  holdingsBefore: Holdings
  plannedBuys: PlannedBuy[]
  completedAt: number
  startedAt: number
  holdingsAfter: any[]
  swapsIn: any[]
}

interface Transfer {
  amount: string
  token: string
  errorId?: string
}

export interface Withdrawal {
  transfers: Transfer[]
  completedAt: number
  startedAt: number
  hasFailedTransfer: boolean
}

export interface HoldingEntry {
  token: string
  netValue: string
  deviation: number
  pool: string
  amount: string
  ticker: string
  netValueUSD?: string
  actualPercent: number
  denomination: number
  realNetValue: string
  realNetValueUSD?: string
  realDeviation: number
  realActualPercent: number
}

interface Holdings {
  totalValueUSD: string
  entries: HoldingEntry[]
  totalValue: string
  isUnbalanced: boolean
  maxDeviation: number
  realTotalValueUSD: string
  realTotalValue: string
  maxRealDeviation: number
}

interface DailyHistory {
  holdings: Holdings
  timestamp: number
  numDeposits: number
  numRebalancings: number
}

interface PortfolioToken {
  weight: number
  ticker: string
  token: string
  pool: string
  denomination: number
}

interface HoldingsData {
  timestamp: number
  totalValue: string
  realTotalValue: string
  entries: HoldingEntry[]
  isUnbalanced: boolean
}

interface Deposit {
  timestamp: number
  amount: string
  usdValue: string
}

export interface Rebalancing {
  swapsIn: any[]
  startedAt: number
  completedAt: number
  holdingsBefore: HoldingsData
  holdingsAfter: HoldingsData
  swapsOut: any[]
}

export async function readAgentInfo(agentId: string): Promise<PortfolioAgentInfo> {
  try {
    const res = await dryrun({
      process: agentId,
      Timestamp: 0,
      tags: [{ name: "Action", value: "Info" }],
    })

    const agentInfo = JSON.parse(res.Messages[0].Data) as PortfolioAgentInfo
    return agentInfo
  } catch (e) {
    console.error(e)
    throw new Error("Agent Info not found")
  }
}

export interface DepositParams {
  quoteTokenId: string
  amount: bigint
  agentId: string
  quoteTokenUsdPrice: number
}
export async function deposit(params: DepositParams): Promise<string> {
  if (typeof params.quoteTokenUsdPrice !== "number")
    throw new Error("Quote token USD price is not a number")

  const { quoteTokenId, amount, agentId, quoteTokenUsdPrice } = params
  const depositId = await transfer(
    {
      token: quoteTokenId,
      recipient: agentId,
      quantity: amount,
      tags: [
        { name: "X-Action", value: "Deposit" },
        { name: "X-Quote-Token-Price", value: String(quoteTokenUsdPrice) },
      ],
    },
    createDataItemSigner(window.arweaveWallet),
  )
  console.log("onTransferId", depositId)

  const computedResult = await result({
    message: depositId,
    process: quoteTokenId,
  })

  console.log("onTransferComputed", computedResult)

  if (computedResult.Messages.length === 0 && computedResult.Output.data) {
    throw new Error(computedResult.Output.data)
  }

  const confirmationId = await retryable(readDepositResult)(depositId)
  console.log("onConfirmationId", confirmationId)

  return depositId
}

export async function readDepositResult(depositId: string): Promise<string> {
  const [, messages] = await getLinkedMessages(undefined, undefined, false, depositId)

  const error = messages.find((msg) => msg.tags["Action"] === "Deposit-Error")
  if (error) throw new BackendError("Deposit failed")

  const confirmation = messages.find((msg) => msg.tags["Action"] === "Confirm-Deposit")
  if (!confirmation) throw new Error("Confirmation not found")

  return confirmation.id
}

export async function togglePause(agentId: string) {
  const requestId = await message({
    process: agentId,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [{ name: "Action", value: "Toggle-Pause" }],
  })
  console.log("onRequestId", requestId)

  const computedResult = await result({
    message: requestId,
    process: agentId,
  })

  console.log("onRequestComputed", computedResult)

  if (computedResult.Messages.length === 0 && computedResult.Output.data) {
    throw new Error(computedResult.Output.data)
  }

  return requestId
}

export async function emergencyExit(agentId: string) {
  const requestId = await message({
    process: agentId,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [{ name: "Action", value: "Emergency-Exit" }],
  })
  console.log("onRequestId", requestId)

  const computedResult = await result({
    message: requestId,
    process: agentId,
  })

  console.log("onRequestComputed", computedResult)

  if (computedResult.Messages.length === 0 && computedResult.Output.data) {
    throw new Error(computedResult.Output.data)
  }

  const unmonitorId = await unmonitorAgent(agentId)
  console.log("onUnmonitorId", unmonitorId)

  return requestId
}

export async function withdraw(agentId: string) {
  const requestId = await message({
    process: agentId,
    signer: createDataItemSigner(window.arweaveWallet),
    tags: [{ name: "Action", value: "Withdraw" }],
  })
  console.log("onRequestId", requestId)

  const computedResult = await result({
    message: requestId,
    process: agentId,
  })

  console.log("onRequestComputed", computedResult)

  if (computedResult.Messages.length === 0 && computedResult.Output.data) {
    throw new Error(computedResult.Output.data)
  }

  const unmonitorId = await unmonitorAgent(agentId)
  console.log("onUnmonitorId", unmonitorId)

  return requestId
}

export type AgentExitPnl = {
  absPnlAllTime: string
  relPnlAllTime: string
}

export async function readExitPnl(agentId: string, quoteTokenPrice: number): Promise<AgentExitPnl> {
  try {
    const res = await dryrun({
      process: agentId,
      Timestamp: 0,
      tags: [
        { name: "Action", value: "Exit-PnL" },
        { name: "Quote-Token-Price", value: String(quoteTokenPrice) },
      ],
    })

    const parsed = JSON.parse(res.Messages[0].Data) as AgentExitPnl
    return parsed
  } catch (e) {
    // console.error(e)
    throw new Error(`Failed to get Agent Exit-PnL for ${agentId}`)
  }
}
